import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
    Cpu, Bot, Activity, Terminal as TerminalIcon,
    Braces, Waypoints, Binary, Network, Fan, Brain,
    Rocket, ChevronRight, Clock, ArrowRight,
    CheckCircle, XCircle, AlertTriangle, Shield,
    Database, FileWarning, List, Globe, Award, Star, Flame
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    } catch (error) {
        console.error('Error formatting time:', error);
        return timestamp.toString();
    }
};

const TokensList = ({ tokens = [] }) => {
    const [tokenConfigs, setTokenConfigs] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTokenConfigs = async () => {
            try {
                const response = await fetch(`${API_URL}/api/configs`);
                if (!response.ok) throw new Error('Failed to fetch configs');
                const data = await response.json();
                if (data.status === 'success') {
                    setTokenConfigs(data.data);
                }
            } catch (error) {
                console.error('Error fetching token configs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTokenConfigs();
    }, []);

    const sortedTokens = [...tokens].sort((a, b) => new Date(a.launch_date) - new Date(b.launch_date));

    const getRankBadge = (index) => {
        switch (index) {
            case 0:
                return (
                    <div className="absolute top-0 right-0 z-10">
                        <div className="flex items-center px-3 py-1 bg-gradient-to-r from-[#1e293b]/95 via-[#2563eb]/95 to-[#1e293b]/95 rounded-bl-xl rounded-tr-xl border-b border-l border-blue-400/30 backdrop-blur-sm group">
                            <Network className="w-3.5 h-3.5 mr-2 text-blue-300" />
                            <span className="font-mono font-bold text-[10px] text-blue-300 tracking-widest group-hover:text-blue-200 transition-colors">
                                GENESIS・01
                            </span>
                        </div>
                    </div>
                );
            default:
                return (
                    <div className="absolute top-0 right-0 z-10">
                        <div className="flex items-center px-3 py-1 bg-gradient-to-r from-[#1e293b]/95 to-[#0f172a]/95 rounded-bl-xl rounded-tr-xl border-b border-l border-blue-500/20 backdrop-blur-sm group">
                            <Binary className="w-3.5 h-3.5 mr-2 text-blue-400" />
                            <span className="font-mono font-bold text-[10px] text-blue-400 tracking-widest group-hover:text-blue-300 transition-colors">
                                #{String(index + 1).padStart(2, '0')}・
                            </span>
                        </div>
                    </div>
                );
        }
    };

    if (loading) {
        return (
            <div
                className="flex items-center justify-center h-screen"
                style={{
                    backgroundImage: `url('/background.jpg')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed'
                }}
            >                <div className="flex flex-col items-center gap-4">
                    <Activity className="w-12 h-12 text-blue-400 animate-spin" />
                    <div className="text-blue-200">Loading...</div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="h-full w-full flex flex-col"
            style={{
                backgroundImage: `url('/background.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed'
            }}
        >
            <div className="h-full flex flex-col w-full max-w-6xl mx-auto px-1 sm:px-4 py-1 sm:py-4">
                {/* Header - Keep fixed */}
                <div className="z-40 mb-1 sm:mb-4 flex-shrink-0">
                    <div className="relative border border-blue-500/30 rounded-lg bg-black/90 backdrop-blur-xl p-2 sm:p-6 w-full">

                        {/* Decorative elements */}
                        <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-blue-500/0" />
                        <div className="absolute top-0 left-0 w-32 h-1 bg-blue-500/50 animate-pulse" />

                        <div className="relative flex flex-col sm:flex-row items-start gap-2 sm:gap-8">
                            {/* Logo & Title Section */}
                            <div className="flex items-center gap-1 sm:gap-4">
                                <div className="relative group">
                                    <div className="absolute -inset-1 sm:-inset-2 bg-gradient-to-r from-blue-500/30 to-blue-400/0 rounded-full blur-lg group-hover:from-blue-400/40 transition-all duration-700" />
                                    <div className="relative w-8 h-8 sm:w-12 sm:h-12 rounded-full border-2 border-blue-400/50 overflow-hidden">
                                        <img
                                            src={process.env.PUBLIC_URL + '/kyora.png'}
                                            alt="Kyora AI"
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent" />
                                    </div>
                                </div>
                                <div>
                                    <h1 className="text-base sm:text-3xl font-mono font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400">
                                        KYORA AI
                                    </h1>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                        <p className="text-[10px] sm:text-sm font-medium tracking-wider text-blue-200/70">NEURAL v1.0</p>
                                        <div className="w-1 h-1 sm:w-2 sm:h-2 rounded-full bg-blue-400/50 animate-pulse" />
                                    </div>
                                </div>
                            </div>

                            {/* Token Stats */}
                            <div className="flex items-center gap-2 sm:gap-4 ml-auto">
                                <div className="flex items-center gap-2">
                                    <Rocket className="w-4 h-4 text-emerald-400" />
                                    <span className="text-[10px] sm:text-sm text-emerald-300">{tokens.length} Tokens</span>
                                </div>
                                <div className="h-6 w-px bg-blue-500/30" />
                                <div className="flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-blue-400" />
                                    <span className="text-[10px] sm:text-sm text-blue-200">Active</span>
                                </div>
                            </div>
                        </div>

                        {/* Description - Only shown on larger screens */}
                        <div className="hidden sm:block mt-6">
                            <p className="text-blue-200/70 text-sm leading-relaxed">
                                "Welcome to my token creation laboratory. Here, you'll find all the digital assets I've carefully engineered
                                through advanced market analysis and blockchain optimization."
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Content - Token Grid with Scrolling */}
                <div className="flex-1 min-h-0 border border-blue-500/30 rounded-lg bg-black/90">
                    <div className="h-full overflow-y-auto px-2 pb-4"
                        style={{
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#3b82f6 #1e293b'
                        }}>
                        {tokens.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                                {sortedTokens.map((token, index) => (
                                    <div
                                        key={token.id}
                                        className="relative group border border-blue-500/20 hover:border-blue-500/40 bg-black/40 rounded-xl overflow-hidden transition-all duration-300"
                                    >
                                        {getRankBadge(index)}
                                        <div className="p-4">
                                            <div className="flex items-start gap-3">
                                                <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-xl border border-blue-500/30 overflow-hidden bg-blue-500/5">
                                                    {tokenConfigs[token.name.toLowerCase()]?.media?.logo ? (
                                                        <img
                                                            src={`${API_URL}${tokenConfigs[token.name.toLowerCase()].media.logo}`}
                                                            alt={token.name}
                                                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                                            onError={(e) => {
                                                                e.target.src = "/placeholder-token.jpg";
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400/50" />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex-1">
                                                    <h3 className="text-sm sm:text-lg font-medium text-blue-200 mb-1 group-hover:text-blue-200 transition-colors">
                                                        {token.name}
                                                    </h3>
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                        <a
                                                            href={token.pumpfun_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center px-2 py-0.5 bg-purple-500/10 hover:bg-purple-500/20 rounded-md text-xs sm:text-sm font-mono text-purple-400 transition-colors"
                                                        >
                                                            ${token.ticker}
                                                        </a>
                                                        <span className="text-xs sm:text-sm text-blue-200/50">
                                                            {new Date(token.launch_date).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="mt-4 flex items-center gap-2">
                                                <Link
                                                    to={`/token-history/${token.id}`}
                                                    className="flex-1 flex items-center justify-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg text-blue-400 text-xs sm:text-sm font-medium transition-colors"
                                                >
                                                    <Activity className="w-3 h-3 sm:w-4 sm:h-4" />
                                                    History
                                                </Link>

                                                <a href={`http://${token.name.toLowerCase().replace(/\s+/g, '-')}.kyora.ai`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex-1 flex items-center justify-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg text-emerald-400 text-xs sm:text-sm font-medium transition-colors"
                                                >
                                                    <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                                                    Website
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="h-full flex items-center justify-center">
                                <div className="relative border border-blue-500/30 rounded-xl bg-black/90 p-8">
                                    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 to-transparent" />
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-blue-500/0" />

                                    <div className="relative flex flex-col items-center gap-6">
                                        <div className="relative">
                                            <div className="absolute -inset-2 bg-blue-500/20 rounded-full blur-lg animate-pulse" />
                                            <Activity className="w-16 h-16 text-blue-400" />
                                        </div>

                                        <div className="text-center space-y-2">
                                            <h3 className="text-xl text-blue-200 font-medium">No Tokens Available</h3>
                                            <div className="flex items-center gap-2 justify-center">
                                                <img
                                                    src={process.env.PUBLIC_URL + '/kyora.png'}
                                                    alt="Kyora"
                                                    className="w-5 h-5 rounded-full border border-blue-400/50"
                                                />
                                                <p className="text-blue-400/70">First token will be created soon</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
};

const TokenCard = ({ token, tokenConfig }) => {
    return (
        <div className="group border border-blue-500/20 hover:border-blue-500/40 bg-black/40 rounded-xl overflow-hidden transition-all duration-300">
            <div className="p-4">
                <div className="flex items-start gap-3">
                    <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-xl border border-blue-500/30 overflow-hidden bg-blue-500/5">
                        {tokenConfig?.media?.logo ? (
                            <img
                                src={tokenConfig.media.logo}
                                alt={token.name}
                                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                onError={(e) => {
                                    e.target.src = "/placeholder-token.jpg";
                                }}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400/50" />
                            </div>
                        )}
                    </div>

                    <div className="flex-1">
                        <h3 className="text-sm sm:text-lg font-medium text-blue-200 mb-1 group-hover:text-blue-200 transition-colors">
                            {token.name}
                        </h3>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <a
                                href={tokenConfig?.pumpfun_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-2 py-0.5 bg-purple-500/10 hover:bg-purple-500/20 rounded-md text-xs sm:text-sm font-mono text-purple-400 transition-colors"
                            >
                                ${token.ticker}
                            </a>
                            <span className="text-xs sm:text-sm text-blue-200/50">
                                {new Date(token.launch_date).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex items-center gap-2">
                    <Link
                        to={`/token-history/${token.id}`}
                        className="flex-1 flex items-center justify-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg text-blue-400 text-xs sm:text-sm font-medium transition-colors"
                    >
                        <Activity className="w-3 h-3 sm:w-4 sm:h-4" />
                        History
                    </Link>

                    <a
                        href={`http://${token.name.toLowerCase().replace(/\s+/g, '-')}.kyora.ai`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg text-emerald-400 text-xs sm:text-sm font-medium transition-colors"
                    >
                        <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                        Website
                    </a>
                </div>
            </div>
        </div>
    );
};

const TokenHistory = () => {
    const { tokenId } = useParams();
    const location = useLocation();
    const [history, setHistory] = useState(null);
    const [tokens, setTokens] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null);
    const [tokenConfigs, setTokenConfigs] = useState({});
    const terminalRef = useRef(null);

    useEffect(() => {
        const fetchTokenConfigs = async () => {
            try {
                const response = await fetch(`${API_URL}/api/configs`);
                if (!response.ok) throw new Error('Failed to fetch configs');
                const data = await response.json();
                if (data.status === 'success') {
                    setTokenConfigs(data.data);
                }
            } catch (error) {
                console.error('Error fetching token configs:', error);
            }
        };

        fetchTokenConfigs();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!tokenId) {
                    const response = await fetch(`${API_URL}/api/token-history`);
                    const data = await response.json();
                    if (data.status === 'success') {
                        setTokens(data.data);
                    } else {
                        setError('Failed to fetch tokens list');
                        setErrorType('fetch_error');
                    }
                } else {
                    const response = await fetch(`${API_URL}/api/token-history/${tokenId}`);
                    const data = await response.json();
                    if (data.status === 'success') {
                        setHistory(data.data);
                    } else {
                        setError(data.message);
                        setErrorType(data.message === 'Token not found' ? 'not_found' : 'fetch_error');
                    }
                }
            } catch (err) {
                console.error('Fetch error:', err);
                setError(err.message);
                setErrorType('fetch_error');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [tokenId]);

    const renderError = () => {
        switch (errorType) {
            case 'not_found':
                return (
                    <div className="relative border border-red-500/30 rounded-lg bg-black/90 p-8 max-w-lg">
                        <div className="flex flex-col items-center gap-6">
                            <Shield className="w-16 h-16 text-red-400" />
                            <div className="text-center">
                                <div className="text-red-300 font-bold text-xl">Neural Link Disrupted</div>
                                <div className="text-red-400/70 text-sm">
                                    <span className="font-mono block mb-2">ERROR CODE: 404_NEURAL_VOID</span>
                                    Token not found in database
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <Database className="w-4 h-4 text-red-400" />
                                <span className="text-red-400/70 font-mono">Token ID: {tokenId}</span>
                            </div>
                        </div>
                    </div>
                );

            case 'list_error':
                return (
                    <div className="relative border border-amber-500/30 rounded-lg bg-black/90 p-8 max-w-lg">
                        <div className="flex flex-col items-center gap-6">
                            <List className="w-16 h-16 text-amber-400" />
                            <div className="text-center">
                                <div className="text-amber-300 font-bold text-xl">Registry Access</div>
                                <div className="text-amber-400/70 text-sm">
                                    Please select a token to view its history
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="relative border border-red-500/30 rounded-lg bg-black/90 p-8 max-w-lg">
                        <div className="flex flex-col items-center gap-6">
                            <AlertTriangle className="w-16 h-16 text-red-400" />
                            <div className="text-center">
                                <div className="text-red-300 font-bold text-xl">System Error</div>
                                <div className="text-red-400/70 text-sm">
                                    Failed to fetch data
                                </div>
                            </div>
                            {error && (
                                <div className="text-red-400/70 text-xs font-mono">
                                    Error: {error}
                                </div>
                            )}
                        </div>
                    </div>
                );
        }
    };

    if (loading) {
        return (
            <div
                className="flex items-center justify-center h-screen"
                style={{
                    backgroundImage: `url('/background.jpg')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed'
                }}
            >                <div className="flex flex-col items-center gap-4">
                    <Activity className="w-12 h-12 text-blue-400 animate-spin" />
                    <div className="text-blue-200">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="flex items-center justify-center h-screen"
                style={{
                    backgroundImage: `url('/background.jpg')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed'
                }}
            >
                {renderError()}
            </div>
        );
    }

    // Show list view when no tokenId is provided
    if (!tokenId && tokens) {
        return <TokensList tokens={tokens} />;
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
            case 'error': return <XCircle className="w-4 h-4 text-red-400" />;
            case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
            default: return <Activity className="w-4 h-4 text-blue-400" />;
        }
    };

    const renderCommand = (command) => {
        // Special handling for initialization
        if (command.command_name === "initialize_token") {
            return (
                <div className="mb-4 leading-relaxed break-words w-full max-w-full">
                    <div className="flex items-center gap-2 text-blue-200/70 mb-1">
                        <span>[{formatTime(command.execution_time)}]</span>
                        <Brain className="w-4 h-4 text-blue-400" />
                    </div>

                    {/* System Message */}
                    <div className="flex items-start gap-3 bg-blue-950/30 rounded-lg p-3 mb-4">
                        <div className="relative flex-shrink-0">
                            <img
                                src={process.env.PUBLIC_URL + '/kyora.png'}
                                alt="Kyora"
                                className="w-8 h-8 rounded-full border border-blue-400/50"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent rounded-full" />
                        </div>
                        <div className="text-blue-200">
                            Neural network initialization sequence started. Creating new token instance in quantum database.
                        </div>
                    </div>

                    {/* System Response */}
                    {command.response && (
                        <div className="text-green-300 bg-green-900/20 rounded p-2 font-mono whitespace-pre max-w-full overflow-x-auto">
                            <div className="overflow-x-auto">
                                {typeof command.response === 'string'
                                    ? command.response
                                    : JSON.stringify(command.response, null, 2)}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="mb-4 leading-relaxed break-words">
                <div className="flex items-center gap-2 text-blue-200/70 mb-1">
                    <span>[{formatTime(command.execution_time)}]</span>
                    <Brain className="w-4 h-4 text-blue-400" />
                </div>

                {/* Command Content */}
                <div className="space-y-4 w-full">
                    {/* Kyora's Thoughts */}
                    {command.result_thought && (
                        <div className="flex items-start gap-3 bg-blue-950/30 rounded-lg p-3 min-w-0 w-full">
                            <div className="relative flex-shrink-0">
                                <img
                                    src={process.env.PUBLIC_URL + '/kyora.png'}
                                    alt="Kyora" className="w-8 h-8 rounded-full border border-blue-400/50" />
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent rounded-full" />
                            </div>
                            <div className="text-blue-200 min-w-0 break-words flex-1">{command.result_thought}</div>
                        </div>
                    )}

                    {command.next_thought && (
                        <div className="flex items-start gap-3 bg-emerald-950/30 rounded-lg p-3">
                            <div className="relative flex-shrink-0">
                                <img
                                    src={process.env.PUBLIC_URL + '/kyora.png'}
                                    alt="Kyora"
                                    className="w-8 h-8 rounded-full border border-emerald-400/50"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/20 to-transparent rounded-full" />
                            </div>
                            <div className="text-emerald-200">{command.next_thought}</div>
                        </div>
                    )}

                    {/* Command Execution */}
                    {command.parameters && (
                        <div className="flex items-start gap-3 bg-yellow-950/30 rounded-lg p-3 min-w-0 w-full">
                            <Rocket className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-yellow-200 mb-2">Executing command:</div>
                                <pre className="text-yellow-300 bg-yellow-900/20 rounded p-2 font-mono whitespace-pre-wrap break-all overflow-x-auto">
                                    {JSON.stringify({
                                        command: command.command_name,
                                        parameters: command.parameters
                                    }, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}

                    {/* Command Response */}
                    {command.response && (
                        <div className="w-full min-w-0">
                            <pre className="text-green-300 bg-green-900/20 rounded p-2 font-mono whitespace-pre-wrap break-all overflow-x-auto">
                                {typeof command.response === 'string'
                                    ? command.response
                                    : JSON.stringify(command.response, null, 2)}
                            </pre>
                        </div>
                    )}

                    {/* Error Message */}
                    {command.error_message && (
                        <div className="text-red-400 bg-red-900/20 rounded p-2 font-mono whitespace-pre">
                            {command.error_message}
                        </div>
                    )}
                </div>
            </div>
        );
    };
    return (
        <div className="h-full flex flex-col">
            <div className="h-full flex flex-col max-w-6xl mx-auto px-2 sm:px-4 py-2 sm:py-4">
                {/* Header */}
                <div className="z-40 mb-1 sm:mb-4 flex-shrink-0">
                    <div className="relative border border-blue-500/30 rounded-lg bg-black/90 backdrop-blur-xl p-2 sm:p-6">
                        {/* Decorative elements */}
                        <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-blue-500/0" />
                        <div className="absolute top-0 left-0 w-32 h-1 bg-blue-500/50 animate-pulse" />

                        <div className="relative flex flex-col sm:flex-row gap-3 sm:gap-6">
                            {/* Token Info Section */}
                            {history?.token && (
                                <div className="flex items-start gap-3 flex-1">
                                    {/* Token Logo - Visible on all screens */}
                                    <div className="relative flex-shrink-0">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/50 to-blue-300/50 rounded-full blur-lg animate-pulse" />
                                        <div className="relative w-12 h-12 sm:w-32 sm:h-32 rounded-full border-2 border-blue-400/50 overflow-hidden">
                                            {tokenConfigs[history.token.name.toLowerCase()]?.media?.logo ? (
                                                <img
                                                    src={`${API_URL}${tokenConfigs[history.token.name.toLowerCase()].media.logo}`}
                                                    alt={history.token.name}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.src = "/placeholder-token.jpg";
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-blue-500/5">
                                                    <Activity className="w-8 h-8 sm:w-12 sm:h-12 text-blue-400/50" />
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent" />
                                        </div>
                                    </div>

                                    {/* Token Details & Thought */}
                                    <div className="flex-1 min-w-0 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <h2 className="text-lg sm:text-2xl font-bold text-blue-200 truncate">
                                                {history.token.name}
                                            </h2>
                                            <a
                                                href={history.token.pumpfun_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm sm:text-lg text-purple-400/70 hover:text-purple-300 font-mono whitespace-nowrap transition-colors"
                                            >
                                                (${history.token.ticker})
                                            </a>
                                        </div>

                                        <p className="text-[10px] sm:text-base text-blue-200/70 leading-relaxed">
                                            {history.token.summary_process ||
                                                `"Throughout this journal entry, I document my creation process for ${history.token.name}. By analyzing market data and blockchain metrics, I've engineered what I believe to be an optimal token structure."`}
                                        </p>

                                        <div className="flex items-center gap-2 mt-1">
                                            <img
                                                src={process.env.PUBLIC_URL + '/kyora.png'}
                                                alt="Kyora"
                                                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border border-blue-400/50"
                                            />
                                            <span className="text-[8px] sm:text-xs text-blue-400/60">- Kyora AI</span>
                                        </div>

                                    </div>
                                </div>
                            )}

                            {/* Back & Entry Info */}
                            <div className="flex flex-col items-end gap-2 sm:gap-3 mt-2 sm:mt-0 min-w-0">
                                <div className="flex flex-wrap items-center justify-end gap-2 min-w-0 max-w-full">
                                    <Link
                                        to="/token-history"
                                        className="flex-shrink-0 flex items-center gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg text-[10px] sm:text-xs text-blue-400 transition-colors border border-blue-500/30 hover:border-blue-400/50"
                                    >
                                        <ChevronRight className="w-3 h-3 rotate-180" />
                                        <span className="font-mono">Back</span>
                                    </Link>

                                    {history?.token?.pumpfun_url && (
                                        <a
                                            href={history.token.pumpfun_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-shrink-0 flex items-center gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 bg-purple-500/10 hover:bg-purple-500/20 rounded-lg text-[10px] sm:text-xs text-purple-400 transition-colors border border-purple-500/30 hover:border-purple-400/50"
                                        >
                                            <Activity className="w-3 h-3" />
                                            <span className="font-mono">PumpFun</span>
                                        </a>
                                    )}

                                    <a
                                        href={`http://${history?.token?.name.toLowerCase().replace(/\s+/g, '-')}.kyora.ai`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-shrink-0 flex items-center gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg text-[10px] sm:text-xs text-emerald-400 transition-colors border border-emerald-500/30 hover:border-emerald-400/50"
                                    >
                                        <Globe className="w-3 h-3" />
                                        <span className="font-mono">Website</span>
                                    </a>
                                </div>

                                <div className="text-[10px] sm:text-sm text-blue-400/60 font-mono text-right">
                                    <div>ENTRY #{history?.token?.id || '---'}</div>
                                    <div>INDEX: {history?.token?.launch_date ? new Date(history.token.launch_date).toLocaleDateString() : '---'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Terminal */}
                <div className="flex-1 min-h-0 border border-blue-500/30 rounded-lg bg-black/90 flex flex-col">
                    {/* Terminal Header */}
                    <div className="relative px-4 py-2 sm:py-3 border-b border-blue-500/30 bg-blue-950/40">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-blue-500/0" />
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 sm:gap-3">
                                <TerminalIcon className="w-4 h-4 text-blue-400" />
                                <span className="text-xs sm:text-sm font-mono text-blue-200">CREATION HISTORY</span>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-4">
                                <div className="flex items-center gap-2">
                                    <Binary className="w-4 h-4 text-blue-400/70" />
                                    <span className="text-[10px] sm:text-xs font-mono text-blue-200/70">
                                        {history?.creation_history?.length || 0} EVENTS
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Network className="w-4 h-4 text-blue-400/70" />
                                    <span className="text-[10px] sm:text-xs font-mono text-blue-200/70">
                                        {history?.token?.status?.toUpperCase() || 'UNKNOWN'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Terminal Content */}
                    <div className="flex-1 overflow-hidden relative">
                        <div className="absolute top-0 left-0 w-full h-6 bg-gradient-to-b from-black to-transparent z-10 pointer-events-none" />

                        <div
                            ref={terminalRef}
                            className="h-full w-full overflow-y-auto overflow-x-hidden font-mono text-sm px-2 sm:px-4 py-3 scroll-smooth"
                            style={{
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#3b82f6 #1e293b'
                            }}
                        >
                            {history?.creation_history?.map((command, index) => (
                                <div key={index} className="mb-6 last:mb-0">
                                    {renderCommand(command)}
                                </div>
                            ))}
                        </div>

                        <div className="absolute bottom-0 left-0 w-full h-6 bg-gradient-to-t from-black to-transparent z-10 pointer-events-none" />
                    </div>
                </div>

            </div>

            {/* Custom Scrollbar Styles */}
            <style jsx global>{`
                /* Webkit Scrollbar Styles */
                ::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }

                ::-webkit-scrollbar-track {
                    background: #1e293b;
                    border-radius: 4px;
                }

                ::-webkit-scrollbar-thumb {
                    background: #3b82f6;
                    border-radius: 4px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #60a5fa;
                }

                /* Firefox Scrollbar Styles */
                * {
                    scrollbar-width: thin;
                    scrollbar-color: #3b82f6 #1e293b;
                }

                /* Add animation for spin-slow */
                @keyframes spin-slow {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
                
                .animate-spin-slow {
                    animation: spin-slow 3s linear infinite;
                }

                /* Hide scrollbar for Chrome, Safari and Opera */
                .overflow-x-auto::-webkit-scrollbar {
                    height: 6px;
                }

                .overflow-x-auto::-webkit-scrollbar-track {
                    background: rgba(30, 41, 59, 0.5);
                    border-radius: 3px;
                }

                .overflow-x-auto::-webkit-scrollbar-thumb {
                    background: rgba(59, 130, 246, 0.5);
                    border-radius: 3px;
                }

                .overflow-x-auto::-webkit-scrollbar-thumb:hover {
                    background: rgba(96, 165, 250, 0.7);
                }
            `}</style>
        </div >
    );
};

export default TokenHistory;